import React, { Fragment, useEffect, useState } from 'react';
import { reduxForm } from 'redux-form';
import PropTypes from 'prop-types';
import { Tab, Tabs } from 'react-bootstrap';
import { validateSimulationSettings } from 'common/formValidation/settings';
import { simulationsTypes } from 'common/choices/settings/settingsGroups';
import FormFields from '../../FormFields';
import ScpiSettings from './scpi/ScpiSettings';
import PlafondLoyersPinel from './pinel/PlafondLoyersPinel';
import LifeInsuranceSettings from './lifeInsurance/LifeInsuranceSettings';
import CreateNewScpiType from './scpi/CreateNewScpiType';
import CreateNewLifeInsuranceType from './lifeInsurance/CreateNewLifeInsuranceType';
import CreateNewLifeInsuranceBaseProducts from './lifeInsurance/CreateNewLifeInsuranceBaseProducts';
import LegalDisclaimers from './legalDisclaimer/LegalDisclaimers';
import PerSettings from './PER/PerSettings';
import ManagementActSettings from './managementAct/ManagementActSettings';
import CreateNewSupportPER from './PER/CreateNewSupportPER';
import CreateNewContractPER from './PER/CreateNewContractPER';
import CreateNewManagementAct from './managementAct/CreateNewManagementAct';
import ModeForm from './ModeForm';
import CreateNewBusinessPartner from './businessPartner/CreateNewBusinessPartner';
import BusinessPartnerForm from './businessPartner/BusinessPartnerForm';
import useBusinessPartners from './businessPartner/useBusinessPartners';

const PARTNER_TAB = 'businessPartner';

const renderSettings = (type, settings, businessPartners) => {
    switch (type) {
        // Ici Pinel = Denormandie
        case 'pinel':
            return <PlafondLoyersPinel settings={settings} />;
        case 'dismemberedScpi':
        case 'scpi':
            return <ScpiSettings type={type} settings={settings} />;
        case 'lifeInsurance':
            return <LifeInsuranceSettings settings={settings} />;
        case 'per':
            return <PerSettings settings={settings} businessPartners={businessPartners} />;
        case 'managementAct':
            return <ManagementActSettings settings={settings} />;
        case 'legalDisclaimer':
            return <LegalDisclaimers type={type} settings={settings} />;
        default:
            return null;
    }
};

const renderTab = (type, { isPartnerTabActive, businessPartners, saveBusinessPartners, toggleBusinessPartnerActive, settings }) => {
    if (PARTNER_TAB === type) {
        return (
            <Tab eventKey={PARTNER_TAB} title="Partenaire" key={PARTNER_TAB}>
                {/* rendu à la demande : son formulaire ne doit jamais être imbriqué dans celui des settings */}
                {isPartnerTabActive && (
                    <BusinessPartnerForm
                        businessPartners={businessPartners}
                        initialValues={{ businessPartner: businessPartners }}
                        onSubmit={saveBusinessPartners}
                        toggleActive={toggleBusinessPartnerActive}
                    />
                )}
            </Tab>
        );
    }

    return (
        <Tab eventKey={type} title={simulationsTypes[type]} key={type}>
            {/* les champs de settings ont besoin du layout de FormFields : pas de rendu sans lui */}
            {!isPartnerTabActive && renderSettings(type, settings[type], businessPartners)}
        </Tab>
    );
};

const FormInner = ({ initialValues: { simulation: settings }, ...props }) => {
    const [activeForm, setActiveForm] = useState(null);
    const [simulationMode, setSimulationMode] = useState('scpi');
    const settingsTypes = Object.keys(simulationsTypes).filter((type) => settings[type]);
    const [activeTab, setActiveTab] = useState(settingsTypes[0]);
    const { businessPartners, createBusinessPartner, saveBusinessPartners, toggleBusinessPartnerActive } = useBusinessPartners();

    const isPartnerTabActive = PARTNER_TAB === activeTab;

    // Partenaire n'est pas un setting, donc pas présent dans settingsTypes : on l'insère
    // manuellement juste avant Mentions légales
    const legalDisclaimerIndex = settingsTypes.indexOf('legalDisclaimer');
    const tabOrder = -1 === legalDisclaimerIndex
        ? [...settingsTypes, PARTNER_TAB]
        : [
            ...settingsTypes.slice(0, legalDisclaimerIndex),
            PARTNER_TAB,
            ...settingsTypes.slice(legalDisclaimerIndex),
        ];

    useEffect(() => {
        setActiveForm(null);
    }, [simulationMode]);

    const tabs = (
        <Tabs id="simulation-tabs" activeKey={activeTab} onSelect={setActiveTab}>
            {tabOrder.map((type) => renderTab(type, {
                isPartnerTabActive,
                businessPartners,
                saveBusinessPartners,
                toggleBusinessPartnerActive,
                settings,
            }))}
        </Tabs>
    );

    return (
        <Fragment>
            <h1 className="text-center">
                Paramètres de Simulation
            </h1>
            <ModeForm setMode={setSimulationMode} />
            {'scpi' === simulationMode &&
                <div className="container create-btns">
                    <CreateNewScpiType
                        hiddenAll={activeForm && activeForm !== 'scpi'}
                        hideForm={() => setActiveForm(null)}
                        showForm={() => setActiveForm('scpi')}
                        type="scpi"
                        form="simulation-scpi-create"
                    />
                    <CreateNewScpiType
                        hiddenAll={activeForm && activeForm !== 'dismemberedScpi'}
                        hideForm={() => setActiveForm(null)}
                        showForm={() => setActiveForm('dismemberedScpi')}
                        type="dismemberedScpi"
                        form="simulation-dismemberedScpi-create"
                    />
                </div>}
            {'av' === simulationMode &&
                <div className="container create-btns">
                    <CreateNewLifeInsuranceBaseProducts
                        hiddenAll={activeForm && activeForm !== 'baseProduct'}
                        hideForm={() => setActiveForm(null)}
                        showForm={() => setActiveForm('baseProduct')}
                    />
                    <CreateNewLifeInsuranceType
                        baseProducts={settings.lifeInsurance.baseProducts}
                        hiddenAll={activeForm && activeForm !== 'av'}
                        hideForm={() => setActiveForm(null)}
                        showForm={() => setActiveForm('av')}
                    />
                </div>}
            {'per' === simulationMode &&
                <div className="container create-btns">
                    <CreateNewSupportPER
                        hiddenAll={activeForm && activeForm !== 'support'}
                        hideForm={() => setActiveForm(null)}
                        showForm={() => setActiveForm('support')}
                        form="simulation-per-supports-create"
                    />
                    <CreateNewContractPER
                        baseProducts={settings.per.baseProducts}
                        businessPartners={businessPartners}
                        hiddenAll={activeForm && activeForm !== 'per'}
                        hideForm={() => setActiveForm(null)}
                        showForm={() => setActiveForm('per')}
                    />
                </div>}
            {PARTNER_TAB === simulationMode &&
                <div className="container create-btns">
                    <CreateNewBusinessPartner
                        hiddenAll={activeForm && activeForm !== PARTNER_TAB}
                        hideForm={() => setActiveForm(null)}
                        showForm={() => setActiveForm(PARTNER_TAB)}
                        onSubmit={createBusinessPartner}
                    />
                </div>}
            {'managementAct' === simulationMode &&
                <div className="container create-btns">
                    <CreateNewManagementAct
                        hiddenAll={activeForm && activeForm !== 'managementAct'}
                        hideForm={() => setActiveForm(null)}
                        showForm={() => setActiveForm('managementAct')}
                    />
                </div>}
            {/* l'onglet Partenaire s'enregistre seul : on sort alors les onglets du formulaire des settings,
                d'où le "container" qui reprend celui porté par FormFields */}
            {isPartnerTabActive ? <div className="container">{tabs}</div> : (
                <FormFields {...props} back="/admin/settings">
                    {tabs}
                </FormFields>
            )}
        </Fragment>
    );
};

FormInner.propTypes = {
    initialValues: PropTypes.shape({
        simulation: PropTypes.shape({
            lifeInsurance: PropTypes.shape({
                baseProducts: PropTypes.shape({}),
            }),
            per: PropTypes.shape({
                baseProducts: PropTypes.shape({}),
            }),
        }).isRequired,
    }).isRequired,
};

const Form = reduxForm({
    form: 'settings-simulation-edit',
    enableReinitialize: true,
    validate: validateSimulationSettings,
})(FormInner);

export default Form;
